import { createFeatureSelector, createSelector } from "@ngrx/store";
import * as fromMipsPacket from "../../reducers/mips-packet/mips-packet.reducer";
import { MipsPacket } from "@models/mips-stepper.model";
import { selectMipsQuestionnaire, selectIsQuestionnaireLoaded } from "@store/selectors/mips-questionnaire/mips-questionnaire.selectors";

export const selectMipsPacketState =
  createFeatureSelector<fromMipsPacket.State>(
    fromMipsPacket.mipsPacketFeatureKey
  );

export const selectTraditionalPacket = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.traditionalPacket
);

export const selectMvpPacket = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.mvpPacket
);
export const selectActivePacket = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State): MipsPacket | null => {
    const mode = state.multiPacketMode;

    if (!mode.enabled) {
      if (mode.currentType === 'mvp') {
        return state.mvpPacket;
      } else if (mode.currentType === 'traditional') {
        return state.traditionalPacket;
      }
      return state.traditionalPacket || state.mvpPacket;
    }

    if (mode.currentType === 'traditional') {
      return state.traditionalPacket;
    } else if (mode.currentType === 'mvp') {
      return state.mvpPacket;
    }
    return state.traditionalPacket;
  }
);

export const selectPacketId = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.id
);

export const selectMultiPacketMode = createSelector(
  selectMipsPacketState,
  (state) => state.multiPacketMode
);

export const selectIsMultiPacketMode = createSelector(
  selectMultiPacketMode,
  (mode) => mode.enabled
);

export const selectCurrentPacketType = createSelector(
  selectMultiPacketMode,
  (mode) => mode.currentType
);

export const selectNextPacketType = createSelector(
  selectMultiPacketMode,
  (mode) => mode.nextType
);

export const selectHasBothPackets = createSelector(
  selectMultiPacketMode,
  (mode) => !!mode.traditionalPacketId && !!mode.mvpPacketId
);

export const selectTraditionalPacketId = createSelector(
  selectMultiPacketMode,
  (mode) => mode.traditionalPacketId
);

export const selectMvpPacketId = createSelector(
  selectMultiPacketMode,
  (mode) => mode.mvpPacketId
);

export const selectProvidersForSelectedPractice = createSelector(
  selectMipsPacketState,
  (state) => state.providersForSelectedPractice
);

export const selectLoadingProviders = createSelector(
  selectMipsPacketState,
  (state) => state.loadingProviders
);

export const selectAvailableTins = createSelector(
  selectMipsPacketState,
  (state) => state.availableTins
);

export const selectLoadingTins = createSelector(
  selectMipsPacketState,
  (state) => state.loadingTins
);

export const selectPerformanceYear = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.performanceYear
);

export const selectPacketStatus = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.status
);

export const selectEligibility = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.eligibility
);

export const selectTinDetails = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.tinDetails
);

export const selectRelatedOrganizations = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.relatedOrganizations ?? []
);

export const selectSettings = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.settings
);

export const selectQuality = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.quality
);

export const selectPromotingInteroperability = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.promotingInteroperability
);

export const selectImprovementActivities = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.improvementActivities
);

export const selectTraditionalIA = createSelector(
  selectTraditionalPacket,
  (packet: MipsPacket | null) => packet?.improvementActivities
);

export const selectMvpIA = createSelector(
  selectMvpPacket,
  (packet: MipsPacket | null) => packet?.improvementActivities
);

export const selectTraditionalPI = createSelector(
  selectTraditionalPacket,
  (packet: MipsPacket | null) => packet?.promotingInteroperability
);

export const selectMvpPI = createSelector(
  selectMvpPacket,
  (packet: MipsPacket | null) => packet?.promotingInteroperability
);

export const selectDrcfStatus = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.drcfStatus
);

export const selectCurrentStep = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.currentStep
);

export const selectIsLoading = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.isLoading
);

export const selectIsSaving = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.isSaving
);

export const selectIsSubmitting = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.isSubmitting
);

export const selectError = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.error
);

export const selectLoadSuccess = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.loadSuccess
);

export const selectSaveSuccess = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.saveSuccess
);

export const selectSubmitSuccess = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.submitSuccess
);

export const selectStep2Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step2Complete ?? false
);

export const selectStep3Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step3Complete ?? false
);

export const selectStep4Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step4Complete ?? false
);

export const selectStep5Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step5Complete ?? false
);

export const selectStep6Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step6Complete ?? false
);

export const selectStep7Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step7Complete ?? false
);

export const selectStep8Complete = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => packet?.step8Complete ?? false
);

export const selectAllStepsComplete = createSelector(
  selectStep2Complete,
  selectStep3Complete,
  selectStep4Complete,
  selectStep5Complete,
  selectStep6Complete,
  selectStep7Complete,
  selectStep8Complete,
  (step2, step3, step4, step5, step6, step7, step8) =>
    step2 && step3 && step4 && step5 && step6 && step7 && step8
);

export const selectCanSubmitPacket = createSelector(
  selectActivePacket,
  selectAllStepsComplete,
  (packet: MipsPacket | null, allComplete: boolean) =>
    (packet?.canSubmit ?? false) && allComplete
);

export const selectReportingLevel = createSelector(
  selectEligibility,
  (eligibility) => eligibility?.reportingLevel
);

export const selectReportingType = createSelector(
  selectEligibility,
  (eligibility) => eligibility?.reportingType
);

export const selectReportingMethod = createSelector(
  selectEligibility,
  (eligibility) => eligibility?.reportingMethod
);

export const selectIsIndividualReporting = createSelector(
  selectReportingLevel,
  (level) => level === "individual"
);

export const selectIsGroupReporting = createSelector(
  selectReportingLevel,
  (level) => level === "group"
);

export const selectIsSubgroupReporting = createSelector(
  selectReportingLevel,
  (level) => level === "subgroup"
);

export const selectIsTraditionalMips = createSelector(
  selectReportingType,
  (type) => type === "traditional"
);

export const selectIsMvp = createSelector(
  selectReportingType,
  (type) => type === "mvp"
);

export const selectIsBoth = createSelector(
  selectReportingType,
  (type) => type === "both"
);

export const selectTinStatus = createSelector(
  selectTinDetails,
  (tinDetails) => tinDetails?.tinStatus
);

export const selectIsTinPending = createSelector(
  selectTinStatus,
  (status) => status === "pending"
);

export const selectIsTinApproved = createSelector(
  selectTinStatus,
  (status) => status === "approved"
);

export const selectIsTinRejected = createSelector(
  selectTinStatus,
  (status) => status === "rejected"
);

export const selectAllDrcfsSigned = createSelector(
  selectDrcfStatus,
  (drcfStatus) => drcfStatus?.allDocumentsSigned ?? false
);

export const selectCanNavigateToStep = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => (stepNumber: number) => {
    if (!packet) return false;

    if (stepNumber === 1) return true;
    if (stepNumber === 2) return true;
    if (stepNumber === 3) return packet.step2Complete;
    if (stepNumber === 4) return packet.step2Complete && packet.step3Complete;
    if (stepNumber === 5)
      return (
        packet.step2Complete && packet.step3Complete && packet.step4Complete
      );
    if (stepNumber === 6)
      return (
        packet.step2Complete &&
        packet.step3Complete &&
        packet.step4Complete &&
        packet.step5Complete
      );
    if (stepNumber === 7)
      return (
        packet.step2Complete &&
        packet.step3Complete &&
        packet.step4Complete &&
        packet.step5Complete &&
        packet.step6Complete
      );
    if (stepNumber === 8)
      return (
        packet.step2Complete &&
        packet.step3Complete &&
        packet.step4Complete &&
        packet.step5Complete &&
        packet.step6Complete &&
        packet.step7Complete
      );

    return false;
  }
);

export const selectCanProceedToNextStep = createSelector(
  selectActivePacket,
  selectCurrentStep,
  (packet: MipsPacket | null, currentStep: number) => {
    if (currentStep === 1) return true;
    if (!packet) return false;

    switch (currentStep) {
      case 2:
        return packet.step2Complete;
      case 3:
        return packet.step3Complete;
      case 4:
        return packet.step4Complete;
      case 5:
        return packet.step5Complete;
      case 6:
        return packet.step6Complete;
      case 7:
        return packet.step7Complete;
      case 8:
        return packet.step8Complete;
      default:
        return false;
    }
  }
);

export const selectCehrtValidation = createSelector(
  selectMipsPacketState,
  (state) => state.cehrtValidation
);

export const selectIsCehrtValidating = createSelector(
  selectCehrtValidation,
  (validation) => validation.isValidating
);

export const selectIsCehrtValid = createSelector(
  selectCehrtValidation,
  (validation) => validation.isValid
);

export const selectCehrtValidationError = createSelector(
  selectCehrtValidation,
  (validation) => validation.error
);

export const selectPacketMetadata = createSelector(
  selectActivePacket,
  (packet: MipsPacket | null) => {
    if (!packet) return null;

    return {
      id: packet.id,
      orgId: packet.orgId,
      userId: packet.userId,
      performanceYear: packet.performanceYear,
      status: packet.status,
      createdDate: packet.createdDate,
      lastModifiedDate: packet.lastModifiedDate,
      submittedDate: packet.submittedDate,
    };
  }
);

export const selectSendingEnvelope = createSelector(
  selectMipsPacketState,
  (state: fromMipsPacket.State) => state.isSaving
);


export const selectDrcfSigningError = createSelector(
  selectMipsPacketState,
  (state) => state.drcfSigningError
);

export const selectQualityMeasures = createSelector(
  selectMipsPacketState,
  (state) => state.qualityMeasures
);

export const selectQualityMeasuresLoading = createSelector(
  selectMipsPacketState,
  (state) => state.qualityMeasuresLoading
);

export const selectDrcfEnvelopesLoading = createSelector(
  selectMipsPacketState,
  (state) => state.drcfEnvelopesLoading
);

export const selectSubmissionScoreLoading = createSelector(
  selectMipsPacketState,
  (state) => state.submissionSuccessData?.scoreLoading ?? false
);

export const selectPacketOrgQuestionnaire = createSelector(
  selectMipsPacketState,
  (state) => state.packetOrgQuestionnaire
);

export const selectEffectiveQuestionnaire = createSelector(
  selectPacketOrgQuestionnaire,
  selectMipsQuestionnaire,
  selectIsQuestionnaireLoaded,
  (packetOrgQuestionnaire, globalQuestionnaire, isLoaded) =>
    packetOrgQuestionnaire ?? (isLoaded ? globalQuestionnaire : null)
);

export const selectMipsReweighting = createSelector(
  selectMipsPacketState,
  (state) => state.reweighting
);

export const selectMipsReweightingLoading = createSelector(
  selectMipsPacketState,
  (state) => state.reweightingLoading
);

export const selectHardshipFlags = createSelector(
  selectEffectiveQuestionnaire,
  (questionnaire) => ({
    quality: questionnaire?.appliedHardship === 'yes' && questionnaire.hardshipQuality === 'yes',
    pi: questionnaire?.appliedHardship === 'yes' && questionnaire.hardshipPromotingInteroperability === 'yes',
    ia: questionnaire?.appliedHardship === 'yes' && questionnaire.hardShipImprovementActivities === 'yes',
    cost: questionnaire?.appliedHardship === 'yes' && questionnaire.hardshipCost === 'yes',
  })
);

export const selectIsSmallPracticeFromQuestionnaire = createSelector(
  selectEffectiveQuestionnaire,
  (questionnaire) => questionnaire?.specialStatusSmall === 'yes',
);

// Replicates the IA component's estimatedTotalTraditional logic so the dashboard
// gauge uses special-status-aware point values instead of the server's flat rate.
export const selectTraditionalIARawPoints = createSelector(
  selectTraditionalIA,
  selectEffectiveQuestionnaire,
  (ia, questionnaire) => {
    if (!ia?.activities) return null;
    const hasSpecialStatus =
      questionnaire?.specialStatusNonPatientFacing === 'yes' ||
      questionnaire?.specialStatusRural === 'yes' ||
      questionnaire?.specialStatusHpsa === 'yes' ||
      questionnaire?.specialStatusSmall === 'yes';
    const pointsPerActivity = hasSpecialStatus ? 40 : 20;
    const total = ia.activities
      .filter(activity => activity.selected)
      .reduce((sum, activity) => sum + (activity.activityId === 'IA_PCMH' ? 40 : pointsPerActivity), 0);
    return Math.min(total, 40);
  }
);

// MVP always applies the full 40-point rate per activity (special status is always true for MVP).
export const selectMvpIARawPoints = createSelector(
  selectMvpIA,
  (ia) => {
    if (!ia?.activities) return null;
    const total = ia.activities
      .filter(activity => activity.selected)
      .reduce((sum) => sum + 40, 0);
    return Math.min(total, 40);
  }
);

export const selectLastQualityMeasureClinicians = createSelector(
  selectMipsPacketState,
  (state) => state.lastQualityMeasureClinicians
);

export const selectSubmissionSuccessData = createSelector(
  selectMipsPacketState,
  (state) => state.submissionSuccessData
);

export const selectOrgSubmissions = createSelector(
  selectMipsPacketState,
  (state) => state.orgSubmissions
);

export const selectOrgSubmissionsLoading = createSelector(
  selectMipsPacketState,
  (state) => state.orgSubmissionsLoading
);